import {
  ConditionOperatorEnumType,
  type BetweenDate,
  type ConditionOperator,
  type RelativeDate,
} from '@reach/shared-types/conditions';
import type { BuilderConditionValue } from '../segment-builder.types';
import type { DateValueMode } from './types';

type DateValue =
  | BuilderConditionValue
  | BetweenDate['startDate']
  | BetweenDate['endDate']
  | null
  | undefined;

export function isRelativeDate(value: DateValue): value is RelativeDate {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    (value as RelativeDate).type === 'relative'
  );
}

export function isBetweenDate(value: BuilderConditionValue | undefined): value is BetweenDate {
  return typeof value === 'object' && value !== null && 'startDate' in value && 'endDate' in value;
}

function isAbsoluteDate(value: DateValue): value is Date {
  return value instanceof Date;
}

function isAbsoluteDateRange(value: BetweenDate): boolean {
  return isAbsoluteDate(value.startDate) && isAbsoluteDate(value.endDate);
}

function isRelativeDateRange(value: BetweenDate): boolean {
  return isRelativeDate(value.startDate) && isRelativeDate(value.endDate);
}

export function isDateValueCompatibleWithOperator(
  operator: ConditionOperator,
  value: BuilderConditionValue | null | undefined,
): boolean {
  if (value === null || value === undefined) return true;

  switch (operator) {
    case ConditionOperatorEnumType.EQUALS_MONTH_DAY:
    case ConditionOperatorEnumType.NOT_EQUALS:
      return isRelativeDate(value);
    case ConditionOperatorEnumType.EQUALS_MONTH_DAY_YEAR:
    case ConditionOperatorEnumType.GREATER_THAN:
    case ConditionOperatorEnumType.LESS_THAN:
      return isAbsoluteDate(value) || isRelativeDate(value);
    case ConditionOperatorEnumType.BETWEEN_MONTH_DAY:
      return isBetweenDate(value) && isAbsoluteDateRange(value);
    case ConditionOperatorEnumType.BETWEEN_MONTH_DAY_YEAR:
      return isBetweenDate(value) && (isAbsoluteDateRange(value) || isRelativeDateRange(value));
    case ConditionOperatorEnumType.EXISTS:
    case ConditionOperatorEnumType.NOT_EXISTS:
      return false;
    default:
      return true;
  }
}

export function getDateValueMode(value: DateValue): DateValueMode | null {
  if (value instanceof Date) return 'absolute';
  if (isRelativeDate(value)) return 'relative';
  return null;
}
